import Loading from '@/components/Loading/Loading';
import SendCrossDomainResults from '@/components/Team/AdminConsole/InVivoSendValidator/SendCrossDomainResults';
import SendInputFailures from '@/components/Team/AdminConsole/InVivoSendValidator/SendInputFailures';
import SendResultsGrid from '@/components/Team/AdminConsole/InVivoSendValidator/SendResultsGrid';
import {
  findBundleValidationResult,
  findInputFailureResult,
} from '@/components/Team/AdminConsole/InVivoSendValidator/sendResults';
import { _notNil } from '@/littledash';
import type { AgentSessionApiId, AgentSessionV1 } from '@/model/Agent.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { type FC, useMemo } from 'react';
import styles from './AgentSendResults.module.scss';

interface AgentSendResultsProps {
  /** The SEND session whose validation result to fetch and render. */
  sessionApiId: AgentSessionApiId;
  /** Injected by the modal system; closes the modal. */
  closeModal: () => void;
}

/**
 * Modal that renders a SEND bundle validation result. The `send_result` tree node supplies only the
 * session id, so the modal fetches the session and finds the result via
 * {@link findBundleValidationResult}. Unlike {@link AgentReport}, the result is typed findings, not
 * server-rendered HTML, styled with the same components the admin-console screen uses.
 *
 * A run stopped on an unreadable file carries an input-failure result instead, on either execution.
 * Those failures replace the findings and the empty state, because "nothing was checked" must not
 * read as "nothing was wrong".
 */
const AgentSendResults: FC<AgentSendResultsProps> = ({ sessionApiId, closeModal }) => {
  const { response, loading } = useApiHook({
    endpoint: 'GET /api/v1/ai-agents/sessions/{sessionApiId}',
    path: { sessionApiId },
    options: {
      onError: {
        throw: false,
        preserveOriginalError: false,
        toast: false,
        capture: true,
        slug: 'agent-send-results-load',
      },
    },
  });

  const session = useMemo(() => (response?.type === 'success' ? (response.body as AgentSessionV1) : null), [response]);
  const inputFailure = useMemo(() => findInputFailureResult(session), [session]);
  const result = useMemo(() => findBundleValidationResult(session), [session]);

  if (loading) {
    return <Loading txt="Loading results" />;
  }

  return (
    <ModalContainer size="wide" testKey="agent-send-results-modal">
      <ModalHeader
        title={_notNil(inputFailure) ? 'SEND files could not be read' : 'SEND validation results'}
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
      />
      <div className={styles.body} data-testid="agent-send-results-content">
        {response?.type === 'error' ? (
          <p className="gray tc" data-test-element="error">
            Couldn't load the validation results.
          </p>
        ) : _notNil(inputFailure) ? (
          <SendInputFailures failures={inputFailure.failures} testId="agent-send-results__input-failures" />
        ) : _notNil(result) ? (
          <div className="flex flex-column" data-test-element="results">
            <SendCrossDomainResults
              groups={result.cross_domain}
              summary={result.summary}
              truncation={result.truncation}
            />
            {result.per_domain.map((perDomain) => (
              <div key={perDomain.domain_code} className="mt3" data-test-element="per-domain-result">
                <SendResultsGrid result={perDomain} testId={`agent-send-results__results--${perDomain.domain_code}`} />
              </div>
            ))}
          </div>
        ) : (
          <p className="gray tc" data-test-element="empty-state">
            No validation results.
          </p>
        )}
      </div>
      <div className="pa3 bt b--moon-gray">
        <ModalActions onCancel={closeModal} cancelBtnText="Close" />
      </div>
    </ModalContainer>
  );
};

export default AgentSendResults;
